import React, { useEffect, useState, useMemo, useReducer } from 'react';
import { ToastContainer } from 'react-toastify';
import { Link } from 'react-router-dom';

//redux
import { useSelector, useDispatch } from 'react-redux';
import Filter, { getFilters } from '../../Components/Common/Filter';

import { createSelector } from 'reselect';
import { withTranslation } from 'react-i18next';
import { getOrdersFilters, getOrders } from '../../slices/orders/thunk';
import {
  formatDocument,
  formatDate,
  formatCurrency,
} from '../../helpers/format';
import { isEmpty } from 'lodash';
import { Can } from '../../helpers/casl';
import TableOrderManagement from './components/TableOrderManagement';
import { translations } from './statuses';
import StatusDropdown from './details/components/StatusDropdown';
import useQsParams from '../../Components/Hooks/QueryString';
import { cleanupStoreHeaders } from '../../helpers/api_helper';
import { translationsStatus } from './statuses-order';
import { UncontrolledTooltip } from 'reactstrap';

const OrdersManagement = (props) => {
  const dispatch = useDispatch();
  const { searchParams, setSearchParams } = useQsParams();

  const orderState = createSelector(
    (state) => state.Order.orders,
    (orders) => orders
  );

  const loadingState = createSelector(
    (state) => state.Order.isLoading,
    (isLoading) => isLoading
  );

  const metaState = createSelector(
    (state) => state.Order.meta,
    (meta) => meta
  );

  const filtersState = createSelector(
    (state) => state.Order.filters,
    (filters) => filters
  );

  const orders = useSelector(orderState);
  const isLoading = useSelector(loadingState);
  const meta = useSelector(metaState);
  const orderFilters = useSelector(filtersState);
  const [filtersParams, setFiltersParams] = useState({});

  useEffect(() => {
    getData();
    dispatch(getOrdersFilters());
  }, [dispatch, searchParams.toString()]);

  const filters = [
    {
      field: 'total',
      name: 'Total da venda',
      type: 'range',
    },

    {
      field: 'generated_cashback',
      name: 'Cashback gerado',
      type: 'range',
    },

    {
      field: 'used_cashback',
      name: 'Cashback consumido',
      type: 'range',
    },

    {
      field: 'cashback_available',
      name: 'Cashback disponível',
      type: 'range',
    },

    {
      field: 'customer_cellphone',
      name: 'Telefone completo',
      type: 'radio',
    },

    {
      field: 'customer_cellphone_verified',
      name: 'Telefone validado',
      type: 'radio',
    },

    {
      field: 'customer_email',
      name: 'Cliente com e-mail',
      type: 'radio',
    },

    {
      field: 'origin',
      name: props.t('order-origin'),
      type: 'checkbox',
    },
    {
      field: 'channel',
      name: props.t('order-channel'),
      type: 'checkbox',
    },
    {
      field: 'seller',
      name: props.t('order-seller'),
      type: 'checkbox',
    },

    {
      field: 'customer_birthdate',
      name: 'Data de aniversário',
      type: 'date',
      options: {
        maxDate: false,
      },
    },

    {
      field: 'cashback_expires_at',
      name: 'Data expiração cashback',
      type: 'date',
      options: {
        maxDate: false,
      },
    },

    {
      field: 'availability_date',
      name: 'Data disponibilização cashback',
      type: 'date',
      options: {
        maxDate: false,
      },
    },

    {
      field: 'created_at',
      name: 'Data da venda',
      type: 'date',
    },
  ];

  const orderCashbackStatus = [
    {
      label: props.t(`orders-management.cashback-status-available`),
      value: 'available',
    },
    {
      label: props.t(`orders-management.cashback-status-used`),
      value: 'used',
    },
    {
      label: props.t(`orders-management.cashback-status-partially-used`),
      value: 'partially_used',
    },
    {
      label: props.t(`orders-management.cashback-status-canceled`),
      value: 'canceled',
    },
    {
      label: props.t(`orders-management.cashback-status-created`),
      value: 'created',
    },
    {
      label: props.t(`orders-management.cashback-status-error`),
      value: 'error',
    },
    {
      label: props.t(`orders-management.cashback-status-expired`),
      value: 'expired',
    },
    {
      label: props.t(`orders-management.cashback-status-pending`),
      value: 'pending',
    },
    {
      label: props.t(`orders-management.cashback-status-released`),
      value: 'released',
    },
    {
      label: props.t(`orders-management.cashback-status-scheduled`),
      value: 'scheduled',
    },
    {
      label: props.t(`orders-management.cashback-status-without-cashback`),
      value: 'without_cashback',
    },
  ];

  const orderStatus = [
    {
      label: props.t(`orders-management.status-created`),
      value: 'created',
    },
    {
      label: props.t(`orders-management.status-waiting-payment`),
      value: 'waiting_payment',
    },
    {
      label: props.t(`orders-management.status-paid`),
      value: 'paid',
    },
    {
      label: props.t(`orders-management.status-invoiced`),
      value: 'invoiced',
    },
    {
      label: props.t(`orders-management.status-waiting-delivery`),
      value: 'waiting_delivery',
    },
    {
      label: props.t(`orders-management.status-delivered`),
      value: 'delivered',
    },
    {
      label: props.t(`orders-management.status-finished`),
      value: 'finished',
    },
    {
      label: props.t(`orders-management.status-partially-refunded`),
      value: 'partially_refunded',
    },
    {
      label: props.t(`orders-management.status-refunded`),
      value: 'refunded',
    },
    {
      label: props.t(`orders-management.status-canceled`),
      value: 'canceled',
    },
    {
      label: props.t(`orders-management.status-invalid`),
      value: 'invalid',
    },
  ];

  const getData = () => {
    const params = { page: searchParams.get('page') ?? 1 };
    const appliedFilters = getFilters(filters, [
      { field: 'external_id', type: 'like' },
      { field: 'document', type: 'like' },
      { field: 'status', type: 'in' },
      { field: 'cashback_status', type: 'in' },
      { field: 'customer_cellphone_verified', type: 'isnull' },
      { field: 'customer_cellphone', type: 'isnull' },
      { field: 'customer_email', type: 'isnull' },
      { field: 'store_id', type: 'in' },
    ]);

    const storesIds = appliedFilters?.storeId;
    if (storesIds) {
      sessionStorage.setItem('filterByStores', storesIds?.join(','));
    } else {
      const authUser = JSON.parse(sessionStorage.getItem('authUser'));
      const stores = authUser?.stores;
      const isAdmin =
        authUser?.role?.name === 'Administrador' ||
        authUser?.role?.name === 'AdministradorOckCash' ||
        authUser?.roles?.some(
          (role) => role?.name === 'Administrador' || role?.name === 'AdministradorOckCash'
        );
      if (!isAdmin) {
        sessionStorage.setItem(
          'filterByStores',
          stores?.map((i) => i.id).join(',')
        );
        appliedFilters.store_id = {
          in: stores?.map((i) => i.id),
        };
      }
    }

    if (!isEmpty(appliedFilters)) {
      params.filters = appliedFilters;
    }

    setFiltersParams(params.filters);
    dispatch(getOrders(params));
  };

  const getTranslationOrderCashbackStatus = (status) => {
    return (
      translations[status] ?? {
        label: 'Não mapeado',
        color: 'esquecémo-dinuevo',
      }
    );
  };

  const getTranslationOrderStatus = (status) => {
    return (
      translationsStatus[status] ?? {
        label: 'Não mapeado',
        color: 'esquecémo-dinuevo',
      }
    );
  };

  const accountSettings = useSelector(
    (state) => state.GeneralSettings.accountSettings
  );

  const columns = useMemo(
    () => [
      {
        Header: props.t('ticket'),
        Cell: (props) => (
          <Link
            to={`${props?.row?.original?.id}`}
            style={{ borderBottom: '1px solid lightgray' }}
          >
            {props?.row?.original?.ticket}
          </Link>
        ),
        reportProps: {
          accessor: 'ticket',
        },
      },
      {
        Header: props.t('split-id'),
        accessor: 'ticketSplitId',
        filterable: false,
        Cell: (props) => props.row.original.ticketSplitId || '-',
      },
      {
        Header: props.t('document'),
        accessor: 'document',
        filterable: false,
        Cell: (props) => {
          if (props?.row?.original?.document)
            return formatDocument(props.row.original.document, accountSettings);

          return '-';
        },
        reportProps: {
          formatter: {
            type: 'mask',
            properties: { custom: 'document' },
          },
        },
      },
      {
        Header: props.t('order-date'),
        accessor: 'date',
        filterable: false,
        Cell: (props) => formatDate(props.row.original.date),
        reportProps: {
          formatter: {
            type: 'date',
            properties: { tz: 'America/Sao_Paulo', format: 'DD/MM/YYYY' },
          },
        },
      },
      {
        Header: props.t('order-origin'),
        accessor: 'origin',
        filterable: false,
        Cell: (info) => {
          const value = info.row.original.origin || '-';
          if (value !== '-') {
            return props.t(`order-origin-${value}`);
          }

          return value;
        },
        reportProps: {
          accessor: 'origin',
          formatter: {
            type: 'enum',
            properties: {
              options: [
                {
                  key: 'vtex',
                  value: props.t('order-origin-vtex'),
                },
                {
                  key: 'panel_ock',
                  value: props.t('order-origin-panel_ock'),
                },
                {
                  key: 'linx_pos',
                  value: props.t('order-origin-linx_pos'),
                },
                {
                  key: 'dito',
                  value: props.t('order-origin-dito'),
                },
                {
                  key: 'pdv',
                  value: props.t('order-origin-pdv'),
                },
                {
                  key: 'not_informed',
                  value: props.t('order-origin-not_informed'),
                },
                {
                  key: 'SmartNfe',
                  value: props.t('order-origin-SmartNfe'),
                },
                {
                  key: 'mobility_food',
                  value: props.t('order-origin-mobility_food'),
                },
                {
                  key: 'wake',
                  value: props.t('order-origin-wake'),
                },
              ],
            },
          },
        },
      },
      {
        Header: props.t('order-channel'),
        accessor: 'channel',
        filterable: false,
        Cell: (info) => {
          const value = info.row.original.channel || '-';
          if (value !== '-') {
            return props.t(`order-channel-${value}`);
          }

          return value;
        },
        reportProps: {
          accessor: 'channel',
          formatter: {
            type: 'enum',
            properties: {
              options: [
                {
                  key: 'ecommerce',
                  value: props.t('order-channel-ecommerce'),
                },
                {
                  key: 'store',
                  value: props.t('order-channel-store'),
                },
              ],
            },
          },
        },
      },
      {
        Header: props.t('order-store'),
        accessor: 'store',
        filterable: false,
        Cell: (props) => props.row.original.store || '-',
        reportProps: {
          accessor: 'store',
        },
      },
      {
        Header: props.t('order-seller'),
        accessor: 'seller',
        filterable: false,
        Cell: (props) => props.row.original.seller || '-',
        reportProps: {
          accessor: 'seller',
        },
      },
      {
        Header: props.t('order-total'),
        accessor: 'total',
        filterable: false,
        Cell: (props) => formatCurrency(props.row.original.total),
        reportProps: {
          formatter: {
            type: 'currency',
            properties: {
              currency: 'BRL',
              centsToCurrency: true,
            },
          },
        },
      },
      {
        Header: props.t('status'),
        accessor: 'status',
        filterable: false,
        Cell: (props) => {
          const translation = getTranslationOrderStatus(
            props.row.original.status
          );

          return (
            <React.Fragment>
              <span className={`badge rounded-pill ${translation.color}`}>
                {translation.label}
              </span>
            </React.Fragment>
          );
        },
        reportProps: {
          accessor: 'status',
          formatter: {
            type: 'enum',
            properties: {
              options: [
                {
                  key: 'created',
                  value: getTranslationOrderStatus('created')?.label,
                },
                {
                  key: 'waiting_payment',
                  value: getTranslationOrderStatus('waiting_payment')?.label,
                },
                {
                  key: 'paid',
                  value: getTranslationOrderStatus('paid')?.label,
                },
                {
                  key: 'invoiced',
                  value: getTranslationOrderStatus('invoiced')?.label,
                },
                {
                  key: 'waiting_delivery',
                  value: getTranslationOrderStatus('waiting_delivery')?.label,
                },
                {
                  key: 'delivered',
                  value: getTranslationOrderStatus('delivered')?.label,
                },
                {
                  key: 'finished',
                  value: getTranslationOrderStatus('finished')?.label,
                },
                {
                  key: 'partially_refunded',
                  value: getTranslationOrderStatus('partially_refunded')?.label,
                },
                {
                  key: 'refunded',
                  value: getTranslationOrderStatus('refunded')?.label,
                },
                {
                  key: 'canceled',
                  value: getTranslationOrderStatus('canceled')?.label,
                },
                {
                  key: 'invalid',
                  value: getTranslationOrderStatus('invalid')?.label,
                },
              ],
            },
          },
        },
      },
      {
        Header: props.t('order-generated-cashback'),
        accessor: 'generatedCashback',
        filterable: false,
        Cell: (props) => formatCurrency(props.row.original.generatedCashback),
        reportProps: {
          formatter: {
            type: 'currency',
            properties: {
              currency: 'BRL',
              centsToCurrency: true,
            },
          },
        },
      },
      {
        Header: props.t('order-used-cashback2'),
        accessor: 'usedCashback',
        filterable: false,
        Cell: (props) =>
          ['created', 'waiting_payment'].includes(props.row.original.status)
            ? formatCurrency(0)
            : formatCurrency(props.row.original.usedCashback),
        reportProps: {
          formatter: {
            type: 'currency',
            properties: {
              currency: 'BRL',
              centsToCurrency: true,
            },
          },
        },
      },
      {
        Header: props.t('order-cashback-available'),
        accessor: 'cashbackAvailable',
        filterable: false,
        Cell: (props) => formatCurrency(props.row.original.cashbackAvailable),
        reportProps: {
          formatter: {
            type: 'currency',
            properties: {
              currency: 'BRL',
              centsToCurrency: true,
            },
          },
        },
      },
      {
        Header: () => (
          <>
            <span
              dangerouslySetInnerHTML={{
                __html: props.t('order-reserved-balance'),
              }}
            />
            <i
              className="ri-question-line ms-1"
              style={{ cursor: 'pointer', fontSize: '14px' }}
              id="required-field-info"
            ></i>
            <UncontrolledTooltip placement="top" target="required-field-info">
              {props.t('reserve')}
            </UncontrolledTooltip>
          </>
        ),
        accessor: 'totalBalanceReserved',
        filterable: false,
        Cell: (props) =>
          ['created', 'waiting_payment'].includes(props.row.original.status)
            ? formatCurrency(props.row.original.totalBalanceReserved)
            : formatCurrency(0),
        reportProps: {
          formatter: {
            type: 'currency',
            properties: {
              currency: 'BRL',
              centsToCurrency: true,
            },
          },
        },
      },
      {
        Header: props.t('order-status'),
        accessor: 'cashbackStatus',
        filterable: false,
        Cell: (props) => {
          const translation = getTranslationOrderCashbackStatus(
            props.row.original.cashbackStatus
          );

          return (
            <React.Fragment>
              <span className={`badge rounded-pill ${translation.color}`}>
                {translation.label}
              </span>
            </React.Fragment>
          );
        },
        reportProps: {
          accessor: 'cashbackStatus',
          formatter: {
            type: 'enum',
            properties: {
              options: [
                {
                  key: 'created',
                  value: getTranslationOrderCashbackStatus('created')?.label,
                },
                {
                  key: 'scheduled',
                  value: getTranslationOrderCashbackStatus('scheduled')?.label,
                },
                {
                  key: 'done',
                  value: getTranslationOrderCashbackStatus('done')?.label,
                },
                {
                  key: 'cancelled',
                  value: getTranslationOrderCashbackStatus('cancelled')?.label,
                },
                {
                  key: 'expired',
                  value: getTranslationOrderCashbackStatus('expired')?.label,
                },
                {
                  key: 'invalid',
                  value: getTranslationOrderCashbackStatus('invalid')?.label,
                },
                {
                  key: 'abandoned',
                  value: getTranslationOrderCashbackStatus('abandoned')?.label,
                },
                {
                  key: 'paid',
                  value: getTranslationOrderCashbackStatus('paid')?.label,
                },
                {
                  key: 'delivered',
                  value: getTranslationOrderCashbackStatus('delivered')?.label,
                },
                {
                  key: 'refunded',
                  value: getTranslationOrderCashbackStatus('refunded')?.label,
                },
                {
                  key: 'partially_refunded',
                  value:
                    getTranslationOrderCashbackStatus('partially_refunded')
                      ?.label,
                },
                {
                  key: 'available',
                  value: getTranslationOrderCashbackStatus('available')?.label,
                },
                {
                  key: 'used',
                  value: getTranslationOrderCashbackStatus('used')?.label,
                },
                {
                  key: 'partially_used',
                  value:
                    getTranslationOrderCashbackStatus('partially_used')?.label,
                },
                {
                  key: 'canceled',
                  value: getTranslationOrderCashbackStatus('canceled')?.label,
                },
                {
                  key: 'error',
                  value: getTranslationOrderCashbackStatus('error')?.label,
                },
                {
                  key: 'pending',
                  value: getTranslationOrderCashbackStatus('pending')?.label,
                },
                {
                  key: 'released',
                  value: getTranslationOrderCashbackStatus('released')?.label,
                },
              ],
            },
          },
        },
      },
      {
        Header: props.t('order-cashback-expires-at'),
        accessor: 'cashbackExpiresAt',
        filterable: false,
        Cell: (props) =>
          props.row.original?.cashbackExpiresAt
            ? formatDate(props.row.original.cashbackExpiresAt, 'DD-MM-Y')
            : '-',
        reportProps: {
          formatter: {
            type: 'date',
            properties: { tz: 'America/Sao_Paulo', format: 'DD/MM/YYYY' },
          },
        },
      },
      {
        Header: props.t('order-availability-date'),
        accessor: 'availabilityDate',
        filterable: false,
        Cell: (props) =>
          props.row.original?.availabilityDate
            ? formatDate(props.row.original.availabilityDate, 'DD-MM-Y')
            : '-',
        reportProps: {
          formatter: {
            type: 'date',
            properties: { tz: 'America/Sao_Paulo', format: 'DD/MM/YYYY' },
          },
        },
      },
      {
        id: props.t('name'),
        Header: props.t('name'),
        accessor: 'customerName',
      },
      {
        id: props.t('phone'),
        Header: props.t('phone'),
        accessor: 'customerCellphone',
      },
      {
        id: props.t('verified-phone'),
        Header: props.t('verified-phone'),
        accessor: 'customerCellphoneVerified',
      },
      {
        id: props.t('email'),
        Header: props.t('email'),
        accessor: 'customerEmail',
      },
      {
        id: props.t('birthdate'),
        Header: props.t('birthdate'),
        accessor: 'customerBirthdate',
        reportProps: {
          formatter: {
            type: 'date',
            properties: { tz: 'America/Sao_Paulo', format: 'DD/MM/YYYY' },
          },
        },
      },
      {
        Header: props.t('actions'),
        Cell: (props) => {
          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <Can I="read" a="sales_management">
                <li className="list-inline-item">
                  <Link to={`${props?.row?.original?.id}`}>
                    <i className="ri-eye-fill align-bottom text-muted" />
                  </Link>
                </li>
              </Can>

              <Can I="update" a="sales_management">
                <li className="list-inline-item" title="Edit">
                  <StatusDropdown order={props.row.original} />
                </li>
              </Can>
            </ul>
          );
        },
        reportProps: {
          exclude: true,
        },
      },
    ],
    []
  );

  const hiddenColumns = [
    props.t('name'),
    props.t('phone'),
    props.t('verified-phone'),
    props.t('email'),
    props.t('birthdate'),
  ];

  document.title = 'Gestão de vendas | Opencashback';

  return (
    <React.Fragment>
      <TableOrderManagement
        props={props}
        columns={columns}
        orderCashbackStatus={orderCashbackStatus}
        orderStatus={orderStatus}
        searchParams={searchParams}
        isLoading={isLoading}
        orders={orders}
        filtersParams={filtersParams}
        meta={meta}
        page={searchParams.get('page')}
        setPage={(p) => setSearchParams('page', p)}
        hiddenColumns={hiddenColumns}
      />

      <Filter
        filters={filters}
        values={orderFilters}
        onClearFilter={cleanupStoreHeaders}
        localePrefix="orders-management"
        showUserStoresFilter
        useGroups
      />
    </React.Fragment>
  );
};

export default withTranslation()(OrdersManagement);
